export const LOOKING_FOR_OPTIONS = [
  "Работу",
  "Клиентов",
  "Сотрудников",
  "Партнёров по бизнесу",
  "Инвестора",
  "Эксперта или подрядчика",
  "Новых друзей",
  "Языковой обмен",
  "Спортивную компанию",
  "Компанию для мероприятий",
  "Просто пообщаться",
  "Пару/семью для встреч",
  "Родителей с детьми",
  "Помощь с адаптацией или переездом",
  "Жильё",
  "Узнавать о встречах",
  "Другое",
] as const;

// Типизация тегов «Сейчас актуально» для будущего мэтчинга по разделам.
export type LookingForKind = "person" | "opportunity" | "event";

export const LOOKING_FOR_KIND: Record<(typeof LOOKING_FOR_OPTIONS)[number], LookingForKind> = {
  Работу: "opportunity",
  Клиентов: "person",
  Сотрудников: "person",
  "Партнёров по бизнесу": "person",
  Инвестора: "opportunity",
  "Эксперта или подрядчика": "person",
  "Новых друзей": "person",
  "Языковой обмен": "person",
  "Спортивную компанию": "person",
  "Компанию для мероприятий": "event",
  "Просто пообщаться": "person",
  "Пару/семью для встреч": "person",
  "Родителей с детьми": "person",
  "Помощь с адаптацией или переездом": "opportunity",
  Жильё: "opportunity",
  "Узнавать о встречах": "event",
  Другое: "person",
};

// Теги, при выборе которых показываются уточняющие поля «Кого именно / Зачем».
export const BUSINESS_LOOKING_FOR_TAGS: ReadonlySet<string> = new Set([
  "Работу",
  "Клиентов",
  "Сотрудников",
  "Партнёров по бизнесу",
  "Инвестора",
  "Эксперта или подрядчика",
]);

// «Организатор» проставляется автоматически после публикации первого события —
// вручную не выбирается.
export const ROLE_OPTIONS = ["Организатор", "Предприниматель"] as const;

export const ROLE_LABEL: Record<(typeof ROLE_OPTIONS)[number], string> = {
  Организатор: "Организатор",
  Предприниматель: "Предприниматель",
};

export const INDUSTRY_OPTIONS = [
  "IT и технологии",
  "Недвижимость",
  "Юридические услуги",
  "Финансы и инвестиции",
  "Здоровье и медицина",
  "Красота и wellness",
  "Образование",
  "HoReCa (рестораны, кафе, доставка)",
  "Туризм и гостеприимство",
  "Строительство и ремонт",
  "Маркетинг, дизайн и медиа",
  "Логистика и импорт-экспорт",
  "Розничная торговля / e-commerce",
  "Психология, коучинг и эзотерика",
  "Крипто и Web3",
  "Сообщества",
  "Другое",
] as const;

export const MAX_INDUSTRIES = 5;

export const INTEREST_OPTIONS = [
  "Путешествия",
  "Психология",
  "Эзотерика",
  "Мода / beauty",
  "Лайфстайл / красивые места",
  "Бизнес",
  "AI",
  "Кино / сериалы",
  "Книги",
  "Языки",
  "Спорт",
  "Йога / тело",
  "Семья / дети",
  "Гастро / кафе",
  "Искусство",
  "Музыка / вечеринки",
  "Культура Турции",
  "Животные",
  "Игры / квизы",
] as const;

export const MIN_INTERESTS = 3;

export const LANGUAGE_OPTIONS = [
  "Русский",
  "English",
  "Türkçe",
  "Українська",
  "Deutsch",
  "Français",
  "Español",
  "Italiano",
  "العربية",
  "Другое",
] as const;

export const LANGUAGE_META: Record<string, { flag: string; code: string }> = {
  Русский: { flag: "🇷🇺", code: "RU" },
  English: { flag: "🇬🇧", code: "EN" },
  Türkçe: { flag: "🇹🇷", code: "TR" },
  Українська: { flag: "🇺🇦", code: "UA" },
  Deutsch: { flag: "🇩🇪", code: "DE" },
  Français: { flag: "🇫🇷", code: "FR" },
  Español: { flag: "🇪🇸", code: "ES" },
  Italiano: { flag: "🇮🇹", code: "IT" },
  العربية: { flag: "🇸🇦", code: "AR" },
  Другое: { flag: "🌐", code: "?" },
};

// «Только изучаю» — взаимоисключающее значение. При его выборе остальные снимаются.
export const COFFEE_STATUS_EXCLUSIVE = "пока_не_готов";

export const COFFEE_STATUS_OPTIONS = [
  { value: "открыт_к_знакомствам", label: "Открыт(а) к знакомствам" },
  { value: "только_деловые", label: "Открыт(а) деловым встречам" },
  { value: COFFEE_STATUS_EXCLUSIVE, label: "Пока только изучаю сообщество" },
] as const;

function firstStatus(value: string | string[] | null | undefined): string | null {
  if (!value) return null;
  if (Array.isArray(value)) return value[0] ?? null;
  return value;
}

export function coffeeStatusLabel(value: string | string[] | null | undefined): string {
  const v = firstStatus(value);
  if (!v) return "";
  return COFFEE_STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v;
}

export function coffeeStatusTone(value: string | string[] | null | undefined): string {
  const v = firstStatus(value);
  switch (v) {
    case "открыт_к_знакомствам":
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case COFFEE_STATUS_EXCLUSIVE:
      return "bg-gray-200 text-gray-600 border-gray-300";
    default:
      return "bg-td-soft text-td-dark border-td-line";
  }
}

// Готовность к контакту хранится как массив. Пользователь считается доступным для
// приглашения, если он НЕ выбрал «Пока только изучаю сообщество».
export function canInviteByStatus(status: string[] | string | null | undefined): boolean {
  if (!status) return true;
  const arr = Array.isArray(status) ? status : [status];
  if (arr.length === 0) return true;
  return !arr.includes(COFFEE_STATUS_EXCLUSIVE);
}

// Открыт(а) к знакомствам сегодня — используется на /home.
export function isOpenForCoffee(status: string[] | string | null | undefined): boolean {
  if (!status) return false;
  const arr = Array.isArray(status) ? status : [status];
  return arr.includes("открыт_к_знакомствам");
}

export type Profile = {
  user_id: string;
  full_name: string;
  city: string;
  photo_url: string | null;
  profession: string | null;
  industry: string | null;
  industries: string[] | null;
  interests: string[] | null;
  languages: string[] | null;
  about: string | null;
  can_help_with: string | null;
  looking_for: string | null;
  looking_for_reason?: string | null;
  looking_for_tags: string[] | null;
  coffee_status: string[] | null;
  role_tags: string[] | null;
  social_links: string[] | null;
  telegram: string | null;
  age: number | null;
  gender: string | null;
  origin_city: string | null;
  district?: string | null;
  neighborhood?: string | null;
  group_coffee_opt_in?: boolean;
  group_coffee_slots?: string[] | null;
  time_in_turkey: string | null;
  conversation_topic: string | null;
  username: string;
  has_seen_welcome: boolean;
  has_visited_people?: boolean;
  last_seen_status?: string | null;
  partner_gender_pref?: string | null;
  partner_age_from?: number | null;
  partner_age_to?: number | null;
  visible_in_directory?: boolean;
  visitor_kind?: string | null;
  created_at: string | null;
  expert_category?: string | null;
  expert_categories?: string[] | null;
  expert_offer?: string | null;
  expert_formats?: string[] | null;
  expert_area?: string | null;
  expert_price_from?: number | null;
  expert_price_currency?: string | null;
  expert_link?: string | null;
  expert_hidden?: boolean | null;
  expert_hidden_reason?: string | null;
  email_opt_out?: boolean;
  is_blogger?: boolean | null;
  blog_url?: string | null;
  hosts_for_experts?: boolean | null;
  hosts_for_experts_note?: string | null;
};

export const VISITOR_KIND_OPTIONS = [
  { value: "resident", label: "Живу в Турции" },
  { value: "tourist", label: "Турист (приезжаю временно)" },
] as const;

export function isTourist(p: { visitor_kind?: string | null }): boolean {
  return p.visitor_kind === "tourist";
}

export function profileIndustries(p: Partial<Profile>): string[] {
  const arr = p.industries ?? [];
  if (arr.length > 0) return arr;
  return p.industry ? [p.industry] : [];
}

export const TIME_IN_TURKEY_OPTIONS = [
  "Меньше 1 года",
  "1–3 года",
  "3–5 лет",
  "5–10 лет",
  "10–20 лет",
  "Более 20 лет",
] as const;

/** Сопоставление старых вариантов с новыми диапазонами (данные не теряются). */
const LEGACY_TIME_IN_TURKEY: Record<string, string> = {
  "Меньше 3 месяцев": "Меньше 1 года",
  "3–12 месяцев": "Меньше 1 года",
  "3-12 месяцев": "Меньше 1 года",
  "1–3 года": "1–3 года",
  "1-3 года": "1–3 года",
  "3+ года": "3–5 лет",
  "3+": "3–5 лет",
};

export function normalizeTimeInTurkey(value: string | null | undefined): string {
  const v = (value ?? "").trim();
  if (!v) return "";
  if ((TIME_IN_TURKEY_OPTIONS as readonly string[]).includes(v)) return v;
  return LEGACY_TIME_IN_TURKEY[v] ?? v;
}

export const TURKEY_CITIES = [
  "Стамбул",
  "Анталья",
  "Аланья",
  "Мерсин",
  "Измир",
  "Бодрум",
  "Фетхие",
  "Сиде",
  "Кемер",
  "Анкара",
  "Дидим",
  "Каш",
  "Белек",
  "Мармарис",
  "Кушадасы",
  "Чешме",
  "Трабзон",
  "Бурса",
  "Адана",
  "Анамур",
  "Газиантеп",
  "Гёчек",
  "Даламан",
  "Денизли",
  "Измит",
  "Кайсери",
  "Калкан",
  "Каппадокия",
  "Конья",
  "Мардин",
  "Невшехир",
  "Олюдениз",
  "Самсун",
  "Чанаккале",
  "Эдирне",
  "Эскишехир",
  "Ялова",
] as const;
